import React, { useState } from 'react';
import { Check, Gift, Compass, Sparkles, ArrowRight, CreditCard, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { PRICING_TIERS } from '../mock/mock';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getPendingReferralCode } from '../context/AuthContext';

const ICONS = { Gift, Compass, Sparkles };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Pricing({ compact = false }) {
  const [annual, setAnnual] = useState(true);
  const [loadingTier, setLoadingTier] = useState(null);
  const navigate = useNavigate();

  const startCheckout = async (tier) => {
    if (tier.id === 'free') {
      navigate('/build');
      return;
    }
    const package_id = `${tier.id}_${annual ? 'annual' : 'monthly'}`;
    setLoadingTier(tier.id);
    try {
      const userKey = localStorage.getItem('tfu_session') || (() => {
        const k = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('tfu_session', k);
        return k;
      })();
      const referralCode = getPendingReferralCode();
      const res = await axios.post(
        `${API}/checkout/session`,
        {
          package_id,
          origin_url: window.location.origin,
          user_key: userKey,
          referral_code: referralCode || undefined,
        },
        { withCredentials: true }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Could not start checkout. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Checkout failed');
      setLoadingTier(null);
    }
  };

  return (
    <section id="pricing" className="relative py-24 lg:py-32 bg-neutral-50">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight">
            Transparent pricing for every builder
          </h2>
          <p className="mt-4 text-lg text-neutral-600">
            Choose the plan that fits your building ambitions. From weekend projects to enterprise applications, we've got you covered.
          </p>
        </div>

        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="inline-flex items-center gap-3 bg-white border border-neutral-200 rounded-full px-5 py-2 shadow-sm">
            <span className={`text-sm font-medium ${!annual ? 'text-neutral-900' : 'text-neutral-500'}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} />
            <span className={`text-sm font-medium ${annual ? 'text-neutral-900' : 'text-neutral-500'}`}>
              Annual <span className="text-emerald-600 text-xs">save up to 16%</span>
            </span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-xs text-neutral-500 mb-10">
          <CreditCard className="w-3.5 h-3.5" />
          <span>Secure checkout via Stripe · Cards accepted (PayPal available in production)</span>
        </div>

        <div className="grid md:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {PRICING_TIERS.map((tier) => {
            const Icon = ICONS[tier.icon];
            const price = annual ? tier.annual : tier.monthly;
            const isLoading = loadingTier === tier.id;
            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-3xl p-7 border transition-all ${
                  tier.highlight
                    ? 'border-neutral-900 shadow-xl ring-1 ring-neutral-900/10'
                    : 'border-neutral-200 hover:border-neutral-300 hover:shadow-md'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-neutral-900 text-white text-[11px] font-semibold tracking-wide">
                    MOST POPULAR
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <h3 className="font-display text-2xl font-bold text-neutral-900">{tier.name}</h3>
                  <Icon className="w-5 h-5 text-neutral-700" />
                  <div className="ml-auto">
                    {annual && tier.save && (
                      <span className="text-xs px-2 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold">
                        Save ${tier.save}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-neutral-600 mt-2">{tier.tagline}</p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold text-neutral-900">${price}</span>
                  <span className="text-neutral-500 text-sm">/ month</span>
                </div>
                {tier.id !== 'free' && annual && (
                  <p className="text-xs text-neutral-500 mt-1">
                    Billed ${price * 12} annually
                  </p>
                )}

                <div className="mt-6 space-y-3">
                  {tier.everythingIn && (
                    <p className="text-sm font-medium text-neutral-700">Everything in {tier.everythingIn}, plus:</p>
                  )}
                  {tier.features.map((f) => (
                    <div key={f} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 text-neutral-900 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-neutral-700">{f}</span>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={() => startCheckout(tier)}
                  disabled={isLoading}
                  className={`mt-7 w-full h-12 rounded-2xl group ${
                    tier.highlight
                      ? 'bg-neutral-900 hover:bg-neutral-800 text-white'
                      : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-900'
                  }`}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…
                    </>
                  ) : tier.id === 'free' ? (
                    <>
                      Try Transformerus
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  ) : (
                    <>
                      Pay with Card
                      <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                    </>
                  )}
                </Button>

                {tier.id !== 'free' && (
                  <div className="mt-3 flex items-center justify-center gap-3 text-[11px] text-neutral-400">
                    <span>Visa</span>
                    <span>·</span>
                    <span>Mastercard</span>
                    <span>·</span>
                    <span>Amex</span>
                    <span>·</span>
                    <span>Apple Pay</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!compact && (
          <p className="text-center text-sm text-neutral-500 mt-10">
            Need a custom plan? <a href="#enterprise" className="text-neutral-900 font-medium underline underline-offset-4">Talk to sales</a>
          </p>
        )}
      </div>
    </section>
  );
}
